/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : userUtils.ts
// 사용자정보 유틸리티 함수
//  - ifUserInfo : 사용자 정보 타입 정의
//  - getUserInfo : 사용자 정보 저장/삭제 함수
//  - setLocalStorage : 사용자 정보 저장/삭제 함수
/////////////////////////////////////////////////////////////////////////////////////////////////////////

/** 유틸명 : ifUserInfo
 * 사용자 정보 타입 정의
 * userId: string;       // 사용자 ID
 * userName?: string;    // 사용자 이름
 * email?: string;       // 이메일
 * department?: string;  // 부서 정보 추가
 * position?: string;    // 직책 정보 추가
 * phone?: string;       // 전화번호 정보 추가
 * language?: string;    // 선택된 언어 정보 추가
 * loginTime?: number;   // 로그인 시간 추가
 * database?: string;    // 선택된 데이터베이스 정보 추가
 */
export interface ifUserInfo {
  userId: string;       // 사용자 ID
  userName?: string;    // 사용자 이름
  email?: string;       // 이메일
  department?: string;  // 부서 정보 추가
  position?: string;    // 직책 정보 추가
  phone?: string;       // 전화번호 정보 추가
  language?: string;    // 선택된 언어 정보 추가
  userKind?: string;    // 사용자유형(ZA_USER.USER_KIND, 공통코드 Z0007: SA 시스템마스터/SU 시스템사용자/CA·CM·CU 회사/BA·BM·BU 사업단/PL 플래너) - 화면 표시 제어용(권한 판단 아님)
  loginTime?: number;   // 로그인 시간 추가
  database?: string;    // 선택된 데이터베이스 정보 추가
  sessExtYn?: string;     // 세션 연장 가능 여부(ZA_USER_CONFIG.SESS_EXT_YN, 사용자환경설정 Z3020M1) - 'N'이면 세션 연장 불가
  sessLimitMin?: number;  // 접속제한시간(분, ZA_USER_CONFIG.SESS_LIMIT_MIN)
  menuIdViewYn?: string;  // 메뉴ID 보기 여부(ZA_USER_CONFIG.MENU_ID_VIEW_YN) - 'Y'이면 사이드메뉴에 "메뉴명(MENU_ID)" 표시
  noticeCoYn?: string;    // 공지권한-회사(ZA_USER_CONFIG.NOTICE_CO_YN) - 'Y'이면 전사 공유+사업단 공유 공지 등록 가능
  noticeCmYn?: string;    // 공지권한-공통(ZA_USER_CONFIG.NOTICE_CM_YN) - 'Y'이면 공통 공유 공지 등록 가능(운영사만 설정)
  schdCmYn?: string;      // 일정권한-공통(ZA_USER_CONFIG.SCHD_CM_YN) - 'Y'이면 일정 공개범위에 공통 선택 가능(운영사만 설정)
  noticeBuYn?: string;    // 공지권한-사업단(ZA_USER_CONFIG.NOTICE_BU_YN) - 'Y'이면 자기 사업단 공유 공지만 등록 가능
  schdCoYn?: string;      // 일정권한-회사(ZA_USER_CONFIG.SCHD_CO_YN) - 'Y'이면 일정 공개범위에 전사 선택 가능
  schdBuYn?: string;      // 일정권한-사업단(ZA_USER_CONFIG.SCHD_BU_YN) - 'Y'이면 일정 공개범위에 사업단 선택 가능
  chatReqYn?: string;     // 챗데스크 요청 권한(ZA_USER_CONFIG.CHAT_REQ_YN) - 'Y'이면 새 상담 요청 가능
  coNm?: string;          // 회사명(BA_COMPANY.CO_NM) - 사용자 패널 상단에 항상 표시
  ddCrtYn?: string;       // DD생성 권한(ZA_USER_CONFIG.DD_CRT_YN) - 'Y'이면 메가메뉴에 DD생성 버튼 노출
  chatCnslYn?: string;    // 챗데스크 상담(담당자) 권한(ZA_USER_CONFIG.CHAT_CNSL_YN) - 'Y'이면 상담담당자
  serverInfo?: { coCd?: string; appIp?: string; dbIp?: string; dbNm?: string }; // 서버 접속 정보(사용자환경설정 Z3020M1 [서버 접속 정보 보기]에서 체크된 항목만 내려옴)
  extAccts?: Record<string, string>; // 외부연동 계정(ZA_USER_EXT_ACCT) - { HIWORKS: 'xxx', GMAIL: 'yyy' } 형태
  // 필요에 따라 추가 필드를 여기에 정의
}

/** 유틸명 : getUserInfo()
 ** 저장된 사용자 정보를 가져옵니다.
 ** 저장소 : localStorage
 * @returns 저장된 사용자 정보 JSON 또는 null
 */
export const getUserInfo = (): ifUserInfo | null => {
  try {
    const retUserInfo = localStorage.getItem('userInfo');
    return retUserInfo ? JSON.parse(retUserInfo) : null;
  } catch (error) {
    console.error('Failed to get user info:', error);
    return null;
  }
};

/** 유틸명 : getSolutionTitle()
 ** mainFrame이 apiMainMenuTree로 받아온 .env(SOLUTION_TITLE) 값을 캐싱해둔 것을 가져옵니다.
 ** mainFrame처럼 이미 state로 갖고 있지 않은 다른 화면/컴포넌트(X1002Q1, IframeTab 등)가
 ** 브랜드명이 필요할 때 prop 없이 바로 쓸 수 있게 함 - 로그인 전/아직 못 받아온 경우 빈 문자열.
 ** 저장소 : localStorage
 */
export const getSolutionTitle = (): string => {
  try {
    return localStorage.getItem('solutionTitle') || '';
  } catch {
    return '';
  }
};

/** 유틸명 : setSolutionTitleCache()
 ** mainFrame이 apiMainMenuTree 응답을 받을 때마다(로그인/언어변경 시) 호출해서 캐시를 갱신합니다.
 * @param solutionTitle .env(SOLUTION_TITLE) 값(없으면 빈 문자열)
 ** 저장소 : localStorage
 */
export const setSolutionTitleCache = (solutionTitle: string): void => {
  try {
    localStorage.setItem('solutionTitle', solutionTitle || '');
  } catch { /* 저장소 접근 실패는 무시 */ }
};

/** 유틸명 : setLocalStorage()
 ** 로그인 성공 시 사용자 정보를 저장합니다.
 * @param userInfo 저장할 사용자 정보.
 ** 저장소 : localStorage
 */
export const setLocalStorage = (userInfo: ifUserInfo): void => {
  try {
    const userInfoWithLoginTime = {
      ...userInfo,
      loginTime: Date.now() // 현재 시간을 로그인 시간으로 설정
    };
    localStorage.setItem('userInfo', JSON.stringify(userInfoWithLoginTime));
    // 사용자 정보가 변경되었음을 알리는 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('userInfoChanged', { detail: userInfoWithLoginTime }));
  } catch (error) {
    console.error('Failed to save user info:', error);
  }
};

/** 유틸명 : clearUserInfo()
 ** 로그아웃 시 사용자 정보를 삭제합니다.
 */
export const clearUserInfo = (): void => {
  try {
    localStorage.removeItem('userInfo');
    // 사용자 정보가 삭제되었음을 알리는 커스텀 이벤트 발생
    window.dispatchEvent(new CustomEvent('userInfoChanged', { detail: null }));
  } catch (error) {
    console.error('Failed to clear user info:', error);
  }
};
